import { useCallback, useMemo } from 'react';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { TextCell } from '../../../../components/table/TextCell';
import { itemIdentityOrName } from '../../../../lib/itemIdentity';
import type { RequisitionSlipDetail, RequisitionSlipLine } from '../requisitionSlipTypes';

/**
 * What one slip asks for.
 *
 * **Its own file, because the profile guards with an early return and derives its live lines
 * below it** — `useDataTable` on the page would be a conditional hook.
 *
 * **Searched, sorted and paged IN MEMORY**, which is the 201 file's exemption: the lines
 * arrive WHOLE with the record, so the filter sees everything there is.
 */
export function RequisitionSlipLinesPanel({ slip }: { slip: RequisitionSlipDetail }) {
  /*
    MEMOISED, and that is load-bearing. `filter` returns a new array every render, so an
    unmemoised source changes identity, which changes `fetchPage`'s, which re-runs
    `useDataTable`'s effect every render until React stops it — rendering zero rows and no
    headers.
  */
  const rows = useMemo(() => slip.items.filter((line) => line.isActive), [slip]);

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? rows.filter((line) =>
            [line.sku, line.itemName, line.manufacturer, line.brandName, line.model, line.remarks]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : rows;

      /*
        A COPY, never an in-place `.sort()`. With no search term the filtered array IS the
        memoised source, so sorting in place would reorder the page's own state behind the
        table.

        THE WIRE FORM IS `field:asc` — `useDataTable.toggleSort`'s own spelling, and NOT the
        leading `-` the server-side `ListQuery` uses. Parsed as a `-`, the first order comes
        out right and every click after it falls through to the default while the header goes
        on cycling above it — a sort that is wrong only after the first click looks exactly
        like one that works.
      */
      const [field, direction] = (query.sort ?? '').split(':');
      const descending = direction === 'desc';

      const sorted = field
        ? [...matched].sort((a, b) => compare(a, b, field) * (descending ? -1 : 1))
        : matched;

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [rows],
  );

  /*
    Generous page size, so the pager is a backstop rather than a wall: no realistic slip
    reaches it, and the footer therefore totals the whole slip on the one page it fits on.
  */
  const state = useDataTable<RequisitionSlipLine>({ fetchPage, pageSize: 50 });

  return (
    <DataTablePanel<RequisitionSlipLine>
      id="requisition-slip-lines"
      columns={COLUMNS}
      rowKey={(line) => line.id}
      state={state}
      fill
      /*
        NOTHING ACTS ON A SELECTION — a line is corrected by replacing the whole set through
        the slip's own form, so there is no per-row write for a checkbox to lead to. That is
        the narrow reading of this opt-out rather than "bulk actions are unbuilt".
      */
      selectable={false}
      /*
        A LINE CARRIES NO PROVENANCE WORTH A COLUMN. It is written and replaced with its slip,
        so the creation trio would be three columns repeating the header's own figure.
      */
      otherInfo={() => null}
      auditColumns={false}
      searchPlaceholder="Search by item, SKU or remark"
      emptyMessage="This slip asks for nothing yet."
      emptySearchMessage="No line matches that."

    />
  );
}

function compare(a: RequisitionSlipLine, b: RequisitionSlipLine, field: string): number {
  switch (field) {
    case 'sku':
      return (a.sku ?? '').localeCompare(b.sku ?? '');
    case 'requestedQuantity':
      return a.requestedQuantity - b.requestedQuantity;
    case 'availableAtRequestQuantity':
      return a.availableAtRequestQuantity - b.availableAtRequestQuantity;
    default:
      return identityOf(a).localeCompare(identityOf(b));
  }
}

/** What the anchor LEADS with, so the column orders by what it shows. */
function identityOf(line: RequisitionSlipLine): string {
  return itemIdentityOrName(line, line.itemName ?? line.sku ?? '');
}

const COLUMNS: DataColumn<RequisitionSlipLine>[] = [
  {
    /*
      THE PRODUCT LEADS, not the item TYPE's governed name — an `InventoryItem` has no name
      column of its own, so a slip asking for two laptops would otherwise read as two
      identically-spelled rows. The type sits beneath it, and only where it is not already
      the label: printing one string twice is what `<ProfileIdentity>` suppresses elsewhere.

      STACKED IS LEGAL HERE. The one-column-one-value rule is enforced by a dev error scoped to
      `IndexTableSurfaceContext`, which only the index shells provide; a `<DataTablePanel>`
      does not, and several sibling panels already stack for this reason.
    */
    key: 'itemName',
    header: 'Item',
    width: 280,
    sortable: true,
    render: (line) => (
      <TextCell value={identityOf(line)} />
    ),
  },
  {
    key: 'sku',
    header: 'SKU',
    width: 140,
    sortable: true,
    render: (line) => <TextCell value={line.sku} mono />,
  },
  {
    key: 'unitOfMeasure',
    header: 'Unit',
    width: 80,
    render: (line) => <TextCell value={line.unitOfMeasure} />,
  },
  {
    key: 'requestedQuantity',
    header: 'Requested',
    width: 110,
    sortable: true,
    align: 'right',
    footer: (lines) => String(lines.reduce((total, line) => total + line.requestedQuantity, 0)),
    render: (line) => <TextCell value={String(line.requestedQuantity)} mono numeric />,
  },
  {
    /*
      WHAT WAS ON HAND WHEN THE SLIP WAS SUBMITTED — advisory, never re-read, and zero on a
      draft because nothing has been snapshotted yet. It is the figure that makes the request
      readable: asking for five against one is a different request from five against fifty.

      NO FOOTER. Summing what was available across lines measured in different units produces a
      figure nothing agrees with — TOTAL IT OR REFUSE IT, and this one is refused.
    */
    key: 'availableAtRequestQuantity',
    header: 'Available when asked',
    width: 170,
    sortable: true,
    align: 'right',
    render: (line) =>
      line.availableAtRequestQuantity === 0 && line.requestedQuantity === 0 ? (
        <TextCell value={null} />
      ) : (
        <TextCell value={String(line.availableAtRequestQuantity)} mono numeric />
      ),
  },
  {
    key: 'remarks',
    header: 'Remarks',
    width: 240,
    render: (line) => <TextCell value={line.remarks} />,
  },
];
